import { useState } from 'react';
import { Link } from 'react-router-dom';
import { actionLabel, actorLabel, shortId, statusLabel } from '../../utils/auditActions';
import { roleLabel } from '../../utils/facilityRoles';
import '../../styles/AuditLogTable.css';

function dayInput(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfDay(value) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
}

function who(uid, names) {
    return names[uid] || shortId(uid);
}

function describeDetails(row, names) {
    const d = row.details || {};
    switch (row.action) {
        case 'case.reassign':
            return d.from
                ? `from ${who(d.from, names)} to ${who(d.to, names)}`
                : `from unassigned to ${who(d.to, names)}`;
        case 'user.offboard':
            return `${d.cases_reassigned ?? 0} cases to ${who(d.reassigned_to, names)}`;
        case 'member.role_change':
            return `${roleLabel(d.from)} to ${roleLabel(d.to)}`;
        case 'case.status_change':
            return `${d.from ? statusLabel(d.from) : 'none'} to ${d.to ? statusLabel(d.to) : 'none'}`;
        case 'admin.view_user_cases':
            // break_glass rows record that patient names were shown; plain rows show case counts only.
            // Rows from before break-glass (2026-09-15) have no break_glass key, and names showed by default then.
            if (d.break_glass === undefined) return '';
            if (d.break_glass) {
                return d.reason
                    ? `viewed patient names, reason: ${String(d.reason).replace(/_/g, ' ')}`
                    : 'viewed patient names';
            }
            return 'viewed case counts only';
        default:
            return Object.entries(d)
                .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`)
                .join(', ');
    }
}

function Target({ row, names, linkCases }) {
    if (row.target_type === 'case') {
        const text = `Case ${shortId(row.target_id)}`;
        // A colleague's case opens only with ?owner=<uid>. Only case.reassign rows record the owner.
        const ownerUid = row.details?.owner_uid;
        return linkCases && ownerUid
            ? <Link to={`/case/${row.target_id}?owner=${encodeURIComponent(ownerUid)}`}>{text}</Link>
            : text;
    }
    if (row.target_type === 'user') return who(row.target_id, names);
    return `${row.target_type} ${shortId(row.target_id)}`;
}

export default function AuditLogTable({ load, linkCases = false, showFacility = false }) {
    const today = new Date();
    const [fromDay, setFromDay] = useState(dayInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)));
    const [toDay, setToDay] = useState(dayInput(today));
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const onShow = async () => {
        const end = startOfDay(toDay);
        end.setDate(end.getDate() + 1);
        setBusy(true);
        setError(null);
        try {
            setResult(await load({ from: startOfDay(fromDay).toISOString(), to: end.toISOString() }));
        } catch (e) {
            setResult(null);
            setError(e?.response?.status === 400 && e.response.data?.detail
                ? e.response.data.detail
                : 'Could not load the activity log.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="audit-log">
            <div className="audit-log-controls">
                <label className="audit-log-field">
                    <span>From</span>
                    <input type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} />
                </label>
                <label className="audit-log-field">
                    <span>To</span>
                    <input type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />
                </label>
                <button type="button" className="audit-log-show" onClick={onShow} disabled={busy || !fromDay || !toDay}>
                    {busy ? 'Loading…' : 'Show activity'}
                </button>
            </div>
            {error && <div className="audit-log-error" role="alert">{error}</div>}
            {result && result.rows.length === 0 && (
                <p className="audit-log-empty">No activity in this date range.</p>
            )}
            {result && result.rows.length > 0 && (
                <>
                    <table className="audit-log-table">
                        <thead>
                            <tr>
                                <th>When</th>
                                <th>Who</th>
                                <th>Action</th>
                                <th>Target</th>
                                <th>Details</th>
                                {showFacility && <th>Facility</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {result.rows.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.at ? new Date(row.at).toLocaleString() : ''}</td>
                                    <td>{actorLabel(row.actor_uid, result.names)}</td>
                                    <td>{actionLabel(row.action)}</td>
                                    <td><Target row={row} names={result.names} linkCases={linkCases} /></td>
                                    <td>{describeDetails(row, result.names)}</td>
                                    {showFacility && <td>{row.facility_id || ''}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {result.truncated && (
                        <p className="audit-log-note">
                            Showing the newest 200 rows. Choose a shorter date range to see older rows.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
